import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { ApiProvider } from '@/lib/api';
import { AuthProvider } from '@/lib/auth';
import { ThemeProvider } from '@/lib/theme/theme-context';
import { ToastProvider } from '@/components/ui';
import { CookieConsent } from '@/components/consent';
import { isApiError } from '@/lib/api';
import { router } from './router';
import { SessionScopedCache } from './SessionScopedCache';

/**
 * Build the QueryClient once per app instance. Auth/permission failures are not
 * retried (they will not self-heal); transient errors get a couple of attempts.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (isApiError(error) && (error.isUnauthorized || error.isForbidden || error.isNotFound)) {
            return false;
          }
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Composition root. Provider order matters: theme first (paints tokens), then
 * the query cache, then auth (token source) and the cache's reset when a session
 * ends, then the API client wired to auth, then toasts, and finally the router.
 * The cookie notice sits beside the router (every route, the policy included).
 * O tema é só do console, e o provedor dele mora acima do RouterProvider: ele
 * recebe o próprio roteador para acompanhar cada navegação.
 */
export function App(): JSX.Element {
  const [queryClient] = useState(createQueryClient);

  return (
    <ThemeProvider router={router}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SessionScopedCache>
            <ApiProvider>
              <ToastProvider>
                <RouterProvider router={router} />
                <CookieConsent />
              </ToastProvider>
            </ApiProvider>
          </SessionScopedCache>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
